import Button from '@doordash/component-button'
import { NumberField } from '@doordash/component-fields'
import { Modal } from '@doordash/component-modal'
import { Text, StackChildren, Colors, Spacing } from '@doordash/design-language'
import * as React from 'react'
import { connect } from 'react-redux'
import * as actions from '../../../actions'
import { COUNTRY_SHORTCODES, DASHER_DIRECT_URL } from '../../../constants'
import * as events from '../../../events'
import { StripeCredentials, SupportedCountryCode } from '../../../models/types'
import * as DA from '../../../typeModules/events'
import Form from '../../../utilities/Form'
import bankAccountInfoModal from '../../molecules/bank-account-info-modal'
import { noBankAccountModal } from './no-bank-account-modal'
import t from './strings'
import { LinkButtonContainer } from './styles'
import * as Exp from '../../../experimentConstants'
import { CountryCode, Interpolate } from '@doordash/lib-i18n'
import { i18nKeys } from 'translationTypes'
import i18n from '../../../i18n'
import Separator from '@doordash/component-separator'
import ListCell from '@doordash/component-list-cell'
import { get } from 'lodash'
import {
  countryExcludesRoutingNumber,
  PaymentData,
} from '../../../utilities/bankAccount'

type AddBankAccountModalState = {
  showNoBankModal: boolean
  showBankAccountInfo: boolean
  routingNumberErrors: string
  accountNumberErrors: string
}

type AddBankAccountModalProps = {
  country: CountryCode
  locale?: string
  firstName?: string
  lastName?: string
  dasherId?: any
  updateBankAccount: any
  createBankAccount?: (data: {
    token: any
    isW2Dasher: boolean
    paymentData?: PaymentData
  }) => Promise<any>
  onClose: any
  uniqueLink: string
  getApplicationStatus?: (uniqueLink: string) => any
  stripeCredentials: StripeCredentials
  stripe: any
  isW2Dasher?: boolean
  experiments?: object
  trackBankAccountFormRendered: (
    data: DA.BankAccountFormPropsRendered
  ) => Promise<any>
  trackBankAccountFormClicked: (
    data: DA.BankAccountFormPropsClicked
  ) => Promise<any>
  trackBankAccountFormSubmit: (
    data: DA.BankAccountFormPropsSubmit
  ) => Promise<any>
  trackBankAccountFormError: (
    data: DA.BankAccountFormPropsError
  ) => Promise<any>
  skipStep?: () => Promise<any>
}

export class AddBankAccountModal extends React.Component<
  AddBankAccountModalProps,
  AddBankAccountModalState
> {
  public fields = {
    routingNumber: '',
    routingNumber2: '',
    accountNumber: '',
  }
  public validators = {
    accountNumber: error => (!error ? this.state.accountNumberErrors : ''),
    routingNumber: error => (!error ? this.state.routingNumberErrors : ''),
    routingNumber2: () => null,
  }
  public constructor(props) {
    super(props)
    this.state = {
      showNoBankModal: false,
      showBankAccountInfo: false,
      accountNumberErrors: '',
      routingNumberErrors: '',
    }
  }

  public componentDidMount() {
    const { trackBankAccountFormRendered, country } = this.props
    trackBankAccountFormRendered({
      name: DA.BankAccountFormNamesRendered.BankAccountForm,
      country: COUNTRY_SHORTCODES[country],
    })
  }

  public toggleNoBankModal() {
    const { trackBankAccountFormClicked, country } = this.props

    this.setState({ showNoBankModal: !this.state.showNoBankModal })
    trackBankAccountFormClicked({
      name: DA.BankAccountFormNamesClicked.NoBank,
      country: COUNTRY_SHORTCODES[country],
    })
  }

  get shouldShowBankInfoLabel() {
    const { country } = this.props
    const countriesWithBankAccountInfo = [
      COUNTRY_SHORTCODES.CA,
      COUNTRY_SHORTCODES.US,
    ]

    return countriesWithBankAccountInfo.includes(
      country as SupportedCountryCode
    )
  }

  public toggleShowBankAccountInfo() {
    const { trackBankAccountFormClicked, country } = this.props

    this.setState({ showBankAccountInfo: !this.state.showBankAccountInfo })
    trackBankAccountFormClicked({
      name: DA.BankAccountFormNamesClicked.FindAccount,
      country: COUNTRY_SHORTCODES[country],
    })
  }

  public setBankAccountFormErrors() {
    const { country } = this.props
    let errorMessage

    switch (country) {
      case COUNTRY_SHORTCODES.AU:
        errorMessage = t.errorMessageBSBCodeAccount()
        break
      case COUNTRY_SHORTCODES.CA:
        errorMessage = t.errorMessageTransitAccount()
        break
      case COUNTRY_SHORTCODES.NZ:
        errorMessage = t.errorMessageAccount()
        break
      default:
        errorMessage = t.errorMessageRoutingAccount()
    }

    this.setState({
      routingNumberErrors: errorMessage,
      accountNumberErrors: errorMessage,
    })
  }

  public isValidForm(accountNumber, routingNumber) {
    const { country } = this.props
    if (countryExcludesRoutingNumber(country as SupportedCountryCode)) {
      return accountNumber !== ''
    }
    return accountNumber !== '' && routingNumber !== ''
  }

  public handleSubmit = async data => {
    this.setState({
      accountNumberErrors: '',
      routingNumberErrors: '',
    })
    const { accountNumber, routingNumber } = data

    if (!this.isValidForm(accountNumber, routingNumber)) {
      this.setBankAccountFormErrors()
      return
    }

    const {
      stripeCredentials,
      firstName,
      lastName,
      createBankAccount,
      getApplicationStatus,
      uniqueLink,
      stripe,
      trackBankAccountFormSubmit,
      trackBankAccountFormError,
      country,
      isW2Dasher,
    } = this.props

    let { routingNumber2 } = data

    if (!routingNumber2 || country === COUNTRY_SHORTCODES.US) {
      routingNumber2 = ''
    }

    const payload: PaymentData = {
      country: stripeCredentials.countryShortname,
      currency: stripeCredentials.currencyCode,
      account_number: accountNumber,
      account_holder_name: `${firstName} ${lastName}`,
      account_holder_type: 'individual',
    }

    if (!countryExcludesRoutingNumber(country as SupportedCountryCode)) {
      payload.routing_number = `${routingNumber}${routingNumber2}`
    }

    try {
      const bankToken = await stripe.createToken('bank_account', payload)
      if (bankToken.error) {
        throw bankToken.error
      }
      await createBankAccount({
        token: bankToken.token,
        isW2Dasher,
        paymentData: payload,
      })

      getApplicationStatus(uniqueLink)

      trackBankAccountFormSubmit({
        country: COUNTRY_SHORTCODES[country],
      })
    } catch (e) {
      trackBankAccountFormError({
        country: COUNTRY_SHORTCODES[country],
        name: 'add_bank_account_modal_error',
        error: e.message,
        data: e.code,
      })
      this.setBankAccountFormErrors()

      if (e.type !== 'invalid_request_error') {
        // throw an error for sentry to catch
        throw e
      }
    }
  }

  public render() {
    const { routingNumberErrors, accountNumberErrors } = this.state
    const { country, locale, experiments } = this.props

    let routingNumberLabel = t.routingNumber()
    let routingNumber2Label = ''
    let routingNumberPlaceholder = '110000000'
    let accountNumberLabel = t.checkingAccountNumber()
    let accountNumberPlaceholder = '000123456789'
    let bankInfoLabel = t.whereToFindNumbers()

    if (country === COUNTRY_SHORTCODES.AU) {
      routingNumberLabel = t.bsbCode()
    }

    if (country === COUNTRY_SHORTCODES.NZ) {
      accountNumberLabel = t.bankAccountNumber()
      accountNumberPlaceholder = '000123456789000'
    }

    if (country === COUNTRY_SHORTCODES.CA) {
      routingNumberLabel = t.branchNumber()
      routingNumber2Label = t.financialInstitutionNumber()
      routingNumberPlaceholder = '12345'
      accountNumberLabel = t.accountNumber()
      bankInfoLabel = t.whereToFindTransitNumber()
    }
    const bankAccountSkipExperiment = get(
      experiments,
      Exp.DX_BANK_ACCOUNT_SKIP,
      false
    )

    const shouldRemoveHelp = get(experiments, Exp.HIDE_BANKACCOUNT_HELP, false)
    const skipSubSectioni18nKey = shouldRemoveHelp
      ? i18nKeys.bankOnboarding__bankAccountSkipSubSectionUpdate_noLink
      : i18nKeys.bankOnboarding__bankAccountSkipSubSectionUpdate

    return (
      <Modal
        onClose={this.props.onClose}
        title={t.title()}
        size={Modal.Sizes.Medium}
        hasCloseButton={false}
      >
        {this.state.showNoBankModal &&
          noBankAccountModal({
            onClose: () => this.toggleNoBankModal(),
            isDasherDirectCopy: bankAccountSkipExperiment,
          })}

        {this.state.showBankAccountInfo &&
          bankAccountInfoModal({
            country,
            locale,
            onClose: () => this.toggleShowBankAccountInfo(),
            disablePrimaryAction: bankAccountSkipExperiment,
          })}
        <Form
          onSubmit={this.handleSubmit}
          validators={this.validators}
          fields={this.fields}
        >
          {(fields, handleChange, handleSubmit, { isLoading }) => (
            <form onSubmit={handleSubmit}>
              <StackChildren size={StackChildren.Sizes.Medium}>
                <Text color={Colors.SystemGrey80}>
                  {bankAccountSkipExperiment ? (
                    <Interpolate
                      i18n={i18n}
                      i18nKey={skipSubSectioni18nKey}
                      components={[
                        <Text
                          display={Text.DisplayModes.Inline}
                          color={Colors.SystemGrey80}
                          tag="a"
                          target="_blank"
                          href={DASHER_DIRECT_URL}
                        />,
                      ]}
                    />
                  ) : (
                    t.titleSubSection()
                  )}
                </Text>
                {!bankAccountSkipExperiment && (
                  <Text styles={Text.Styles.Bold18}>{t.bankFormTitle()}</Text>
                )}
                {!countryExcludesRoutingNumber(
                  country as SupportedCountryCode
                ) && (
                  <NumberField
                    label={routingNumberLabel}
                    onChange={handleChange.bind(this, 'routingNumber')}
                    placeholder={
                      bankAccountSkipExperiment ? '' : routingNumberPlaceholder
                    }
                    {...fields.routingNumber}
                    error={routingNumberErrors}
                  />
                )}
                {country === COUNTRY_SHORTCODES.CA && (
                  <NumberField
                    label={routingNumber2Label}
                    onChange={handleChange.bind(this, 'routingNumber2')}
                    placeholder={'123'}
                    {...fields.routingNumber2}
                  />
                )}
                <NumberField
                  label={accountNumberLabel}
                  onChange={handleChange.bind(this, 'accountNumber')}
                  placeholder={
                    bankAccountSkipExperiment ? '' : accountNumberPlaceholder
                  }
                  {...fields.accountNumber}
                  error={accountNumberErrors}
                />
                {bankAccountSkipExperiment ? (
                  <React.Fragment>
                    <Separator />
                    {this.shouldShowBankInfoLabel && (
                      <React.Fragment>
                        <ListCell
                          insetSize={ListCell.InsetSizes.None}
                          title={
                            <Text color={Colors.SystemBlack}>
                              {t.whereToFindNumbersShort()}
                            </Text>
                          }
                          iconEnd={{
                            type: ListCell.IconTypes.ChevronRight,
                            size: Spacing.Medium,
                          }}
                          isSeparatorVisible={false}
                          onClick={() => this.toggleShowBankAccountInfo()}
                          tag="a"
                        />
                        <Separator
                          type={Separator.Types.InsetBorder}
                          verticalMargin={Spacing.Small}
                        />
                      </React.Fragment>
                    )}
                    {!shouldRemoveHelp && (
                      <React.Fragment>
                        <ListCell
                          insetSize={ListCell.InsetSizes.None}
                          title={
                            <Text color={Colors.SystemBlack}>
                              {t.noBankButton()}
                            </Text>
                          }
                          iconEnd={{
                            type: ListCell.IconTypes.ChevronRight,
                            size: Spacing.Medium,
                          }}
                          isSeparatorVisible={false}
                          onClick={() => this.toggleNoBankModal()}
                        />
                        <Separator
                          type={Separator.Types.InsetBorder}
                          verticalMargin={Spacing.Small}
                        />
                      </React.Fragment>
                    )}
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {this.shouldShowBankInfoLabel && (
                      <LinkButtonContainer
                        tag={'a'}
                        weight={Text.Weights.Bold}
                        size={Text.Sizes.S14}
                        styles={Text.Styles.ContentAction}
                        color={Colors.TextAction}
                        onClick={() => this.toggleShowBankAccountInfo()}
                      >
                        {bankInfoLabel}
                      </LinkButtonContainer>
                    )}
                    <LinkButtonContainer
                      tag="a"
                      weight={Text.Weights.Bold}
                      size={Text.Sizes.S14}
                      styles={Text.Styles.ContentAction}
                      color={Colors.TextAction}
                      onClick={() => this.toggleNoBankModal()}
                    >
                      {t.noBankButton()}
                    </LinkButtonContainer>
                  </React.Fragment>
                )}
                <StackChildren size={StackChildren.Sizes.XxSmall}>
                  <Button
                    onClick={async () => {
                      try {
                        await handleSubmit()
                      } catch (e) {
                        console.warn(e)
                      }
                    }}
                    state={
                      isLoading ? Button.States.Loading : Button.States.Default
                    }
                  >
                    {t.submit()}
                  </Button>
                  <Button
                    type={
                      bankAccountSkipExperiment
                        ? Button.Types.Tertiary
                        : Button.Types.FlatPrimary
                    }
                    onClick={
                      bankAccountSkipExperiment
                        ? this.props.skipStep
                        : this.props.onClose
                    }
                  >
                    {bankAccountSkipExperiment ? t.skipForNow() : t.goBack()}
                  </Button>
                </StackChildren>
              </StackChildren>
            </form>
          )}
        </Form>
      </Modal>
    )
  }
}

const mapStateToProps = state => ({
  firstName: state.application.firstName,
  lastName: state.application.lastName,
  country: state.application.country,
  dasherId: state.application.dasherId,
  stripeCredentials: state.stripeCredentials,
  experiments: state.experiments,
})

const mapDispatchToProps = {
  getApplicationStatus: actions.getApplicationStatus,
  createBankAccount: actions.createBankAccount,
  trackBankAccountFormRendered: events.trackBankAccountFormRendered,
  trackBankAccountFormClicked: events.trackBankAccountFormClicked,
  trackBankAccountFormSubmit: events.trackBankAccountFormSubmit,
  trackBankAccountFormError: events.trackBankAccountFormError,
}

export default connect(mapStateToProps, mapDispatchToProps)(AddBankAccountModal)
